import React from "react";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select.tsx";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

import {
  Camera,
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Users,
  MapPin,
  ClipboardPlus,
  ArrowRight,
  Loader2,
  HelpCircle,
  X,
  ShieldCheck,
  ChevronDown,
  Plus,
  Star,
  VolumeX,
  Play,
  ListOrdered,
  PhoneCall,
  Send as SendIcon,
  Sparkles,
} from "lucide-react";
import RegistrationQueueView from "@/components/patient/RegistrationQueueView";
import type { QueueItem } from "@/components/patient/RegistrationQueueView";
import SendLeadRegistrationModal from "@/components/patient/SendLeadRegistrationModal";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import DatePicker from "@/components/custom/DatePicker.tsx";
import { cn, formatPhoneDisplay, formatPhoneDash, handlePhoneInputChange, handlePhoneKeyDown } from "@/lib/utils.ts";
import { format } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout.tsx";
import AuthService from "@/lib/auth";
import biometricAuthService from "@/services/biometricAuthService.ts";
import { toast } from "sonner";
import { useLocation } from "@/contexts/LocationContext";
import EmailService from "@/lib/email";
import { buildRegistrationEmailHtml } from "@/utils/registrationEmail";
import { v4 as uuidv4 } from "uuid";
import { getCurrentUser } from "aws-amplify/auth";

const validationSchema = z.object({
  first_name: z
    .string()
    .min(1, "First name is required")
    .regex(
      /^[A-Za-z\s'-]+$/,
      "First name should not contain numbers or special characters",
    ),
  last_name: z
    .string()
    .min(1, "Last name is required")
    .regex(
      /^[A-Za-z\s'-]+$/,
      "Last name should not contain numbers or special characters",
    ),
  middle_name: z.string().optional(),
  social_security_number: z.string()
    .optional()
    .refine((val) => !val || /^\d{3}-\d{2}-\d{4}$/.test(val), "SSN must be in format 123-45-6789"),
  date_of_birth: z
    .date({ required_error: "Date of birth is required" })
    .max(new Date(), "Date of birth cannot be in the future")
    .refine((dob) => {
      const today = new Date();
      const minDate = new Date(
        today.getFullYear() - 5,
        today.getMonth(),
        today.getDate(),
      );
      return dob <= minDate;
    }, "Patient must be at least 5 years old"),
  mobile_number: z
    .string()
    .refine(
      (v) => {
        const digits = v.replace(/\D/g, "");
        return digits.length === 10 || (digits.length === 11 && digits.startsWith("1"));
      },
      "Mobile number must be exactly 10 digits",
    ),
  gender: z.string({
    required_error: "Gender is required",
  }),
  email: z.union([z.literal(""), z.string().email("Invalid email address")]).optional(),
  hospital_name: z.string().min(1, "Hospital is required"),
  location_ids: z.string().min(1, "Location is required"),
  primary_provider_uuid: z.string().min(1, "Primary Provider is required"),
  secondary_provider_uuid: z.string().optional(),
  address_line1: z.string().min(1, "Address Line 1 is required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z
    .string()
    .min(1, "ZIP Code is required")
    .regex(/^\d{5}(-\d{4})?$/, "Invalid ZIP code — use 12345 or 12345-6789"),
  photo: z.any().optional(),
  selected_tests: z.array(z.string()).optional(),
  mhai_consent: z.boolean().refine((val) => val === true, {
    message: "You must acknowledge and accept the MHAI terms to proceed",
  }),
  hipaa_consent: z.boolean().refine((val) => val === true, {
    message: "You must accept the HIPAA privacy practices to proceed",
  }),
  esign_consent: z.boolean().refine((val) => val === true, {
    message: "You must accept the e-signature terms to proceed",
  }),
  biometric_consent: z.boolean().optional(),
  email_consent: z.boolean().optional(),
  sms_consent: z.boolean().refine((val) => val === true, {
    message: "You must consent to SMS communications to proceed",
  }),
}).refine((data) => {
  if (data.email && data.email.trim() !== "" && !data.email_consent) {
    return false;
  }
  return true;
}, {
  message: "You must consent to email communications to proceed",
  path: ["email_consent"],
});

type RegistrationFormValues = z.infer<typeof validationSchema>;

export default function Create() {
  const { patientId, encounterId } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [faceRegistered, setFaceRegistered] = useState(false);
  const [showFaceIdModal, setShowFaceIdModal] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const { locations, selectedLocationId, selectedHospitalName } = useLocation();


  const [isRecording, setIsRecording] = useState(false);
  const [recordedVideos, setRecordedVideos] = useState<File[]>([]);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null,
  );
  const [recordingTime, setRecordingTime] = useState(0);
  const [showFaceFoundDialog, setShowFaceFoundDialog] = useState(false);
  const [existingFaceData, setExistingFaceData] = useState<any>(null);
  const [recordingPhase, setRecordingPhase] = useState<
    "initial" | "additional"
  >("initial");
  const [statusMessage, setStatusMessage] = useState<string>("");
  const [recordingDisabled, setRecordingDisabled] = useState(false);
  const [hasLoadedExistingData, setHasLoadedExistingData] = useState(false);
  const [hipaaInfoOpen, setHipaaInfoOpen] = useState(false);
  const [openSection, setOpenSection] = useState<'patient' | 'consents' | 'practitioner' | null>('patient');
  // Track which sections have been completed
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());

  // Intro video, shown before the registration form for new patients.
  // entrypoint.sh generates window._env_ from runtime environment variables
  // at container startup (see /usr/share/nginx/html/env-config.js), which is
  // what actually lets App Runner's console variable take effect without a
  // rebuild. import.meta.env is kept as a fallback for local `vite dev`.
  const PATIENT_INTRO_VIDEO_URL =
    (window as any)._env_?.VITE_PATIENT_INTRO_VIDEO_URL ||
    import.meta.env.VITE_PATIENT_INTRO_VIDEO_URL ||
    "https://d4ilgen09xf69.cloudfront.net/patient_onboarding_video_compressed.mp4";
  // Only play the intro for a brand-new registration — skip it when
  // opening an existing patient/encounter for editing (patientId/encounterId
  // present in the URL and not the literal "new" placeholder).
  const isNewRegistration = (!patientId || patientId === "new") && (!encounterId || encounterId === "new");
  // Don't auto-popup video when staff lands on registration tab; available on-demand
  const [showVideoIntro, setShowVideoIntro] = useState(false);
  const videoIntroRef = useRef<HTMLVideoElement>(null);
  const [videoNeedsUnmute, setVideoNeedsUnmute] = useState(false);
  const [videoIsPlaying, setVideoIsPlaying] = useState(false);

  // Try to autoplay the intro video WITH sound. Browsers block this in most
  // cases (no prior user interaction with the site), so we catch the
  // rejection and fall back to muted autoplay + a one-tap "Unmute" button.
  useEffect(() => {
    if (!showVideoIntro) return;
    const vid = videoIntroRef.current;
    if (!vid) return;

    vid.muted = false;
    const playAttempt = vid.play();
    if (playAttempt !== undefined) {
      playAttempt.catch(() => {
        vid.muted = true;
        setVideoNeedsUnmute(true);
        vid.play().catch(() => {
          // Even muted autoplay failed (rare) — user can hit Skip.
        });
      });
    }
  }, [showVideoIntro]);



  // Deferred patient creation — IDs are null until "Next Step" is clicked
  const [createdPatientId, setCreatedPatientId] = useState<string | null>(null);
  const [createdEncounterId] = useState<string | null>(null);
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);

  // Resolve the effective patient/encounter ID regardless of flow
  const effectivePatientId = (!patientId || patientId === "new") ? createdPatientId : patientId;
  const effectiveEncounterId = (!encounterId || encounterId === "new") ? createdEncounterId : encounterId;

  // Track whether registration is in progress to warn on navigation.
  // registrationInProgress is true when patient exists in DB but form not yet submitted.
  const registrationInProgress = !!createdPatientId && !completedSections.has('practitioner');

  // Show a confirmation dialog when the user tries to navigate away mid-registration.
  // Uses a local state flag instead of useBlocker (which requires createBrowserRouter).
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavUrl, setPendingNavUrl] = useState<string | null>(null);

  // Warn on browser tab close / page refresh when registration is in progress.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (registrationInProgress) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [registrationInProgress]);

  // Intercept sidebar <Link> clicks (rendered as <a> tags by React Router).
  // When registration is in progress, capture the href and show the warning
  // dialog instead of navigating away.
  useEffect(() => {
    const handleLinkClick = (e: MouseEvent) => {
      if (!registrationInProgress) return;
      const target = (e.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;
      if (!target) return;
      const href = target.getAttribute("href");
      if (!href || href.startsWith("http") || href.startsWith("mailto")) return;
      // Only intercept internal app links — not the form's own submit buttons
      const isInsideForm = (e.target as HTMLElement).closest("form") !== null;
      if (isInsideForm) return;
      e.preventDefault();
      setPendingNavUrl(href);
      setShowLeaveWarning(true);
    };
    document.addEventListener("click", handleLinkClick, true);
    return () => document.removeEventListener("click", handleLinkClick, true);
  }, [registrationInProgress]);

  const [primaryProviders, setPrimaryProviders] = useState<any[]>([]);
  const [primaryProvidersLoading, setPrimaryProvidersLoading] = useState(true);

  const isDialogOpenRef = useRef(true);
  const recordingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
    watch,
  } = useForm<RegistrationFormValues>({
    resolver: zodResolver(validationSchema),
    shouldUnregister: false,
    defaultValues: {
      first_name: "",
      last_name: "",
      date_of_birth: undefined,
      gender: "",
      mobile_number: "",
      photo: "",
      email: "",
      hospital_name: "",
      location_ids: "",
      primary_provider_uuid: "",
      secondary_provider_uuid: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip: "",
      mhai_consent: false,
      hipaa_consent: false,
      esign_consent: false,
      biometric_consent: false,
      email_consent: false,
      sms_consent: false,
      middle_name: "",
      social_security_number: "",
    },
  });

  const [activeMainTab, setActiveMainTab] = useState<'register' | 'queue'>('register');
  const [registrationMode, setRegistrationMode] = useState<'in-clinic' | 'phone-call'>('phone-call');
  const [sendLinkModalOpen, setSendLinkModalOpen] = useState(false);
  const [reviewingQueueItem, setReviewingQueueItem] = useState<QueueItem | null>(null);
  const [queueBadgeCount, setQueueBadgeCount] = useState(0);

  const fetchQueueBadge = () => {
    AuthService.getRegistrationQueue('submitted')
      .then((res: any) => {
        if (res?.data?.submitted_count !== undefined) {
          setQueueBadgeCount(res.data.submitted_count);
        }
      })
      .catch(() => { });
  };

  useEffect(() => {
    fetchQueueBadge();
  }, [activeMainTab]);

  const handleReviewFromQueue = (item: QueueItem) => {
    setReviewingQueueItem(item);
    setActiveMainTab('register');
    setRegistrationMode('in-clinic');

    const d = item.patient_data || {};
    const fName = d.first_name || item.first_name || "";
    const mName = d.middle_name || item.middle_name || "";
    const lName = d.last_name || item.last_name || "";

    if (fName) setValue("first_name", fName);
    if (mName) setValue("middle_name", mName);
    if (lName) setValue("last_name", lName);

    if (d.date_of_birth) setValue("date_of_birth", new Date(d.date_of_birth));
    if (d.gender) setValue("gender", d.gender);

    const mobileToUse = d.mobile_number || item.phone || "";
    if (mobileToUse) {
      setValue("mobile_number", formatPhoneDash(mobileToUse));
    }

    const emailToUse = d.email || item.email || "";
    if (emailToUse) setValue("email", emailToUse);

    if (d.social_security_number) setValue("social_security_number", d.social_security_number || "");
    if (d.address_line1) setValue("address_line1", d.address_line1);
    if (d.address_line2) setValue("address_line2", d.address_line2 || "");
    if (d.city) setValue("city", d.city);
    if (d.state) setValue("state", d.state);
    if (d.zip) setValue("zip", d.zip);
    setValue("hipaa_consent", true);
    setValue("esign_consent", true);
    setValue("mhai_consent", true);
    setValue("sms_consent", true);
    setValue("email_consent", !!d.email_consent);
    setValue("biometric_consent", !!d.biometric_consent);

    setCompletedSections(new Set(['patient', 'consents']));
    setOpenSection('practitioner');
    toast.info(`Reviewing profile for ${item.derived_patient_name}. Demographics loaded. Complete Care Practitioner details below.`);
  };

  // Sync global location context to form state
  useEffect(() => {
    if (selectedLocationId) {
      setValue("location_ids", String(selectedLocationId));
    }
    if (selectedHospitalName) {
      setValue("hospital_name", selectedHospitalName);
    }
  }, [selectedLocationId, selectedHospitalName, setValue]);

  const [filteredLocations, setFilteredLocations] = useState<any[]>([]);


  // Sync filtered locations if locations from context changes or hospital is already selected
  useEffect(() => {
    const selectedHospital = watch("hospital_name");
    if (selectedHospital && locations.length > 0) {
      const filtered = locations.filter(l => l.hospitalName === selectedHospital);
      setFilteredLocations(filtered);
    }
  }, [locations, watch("hospital_name")]);

  // Sync hospital_name if location_ids is pre-filled from existing data
  useEffect(() => {
    const selectedLocId = watch("location_ids");
    if (selectedLocId && locations.length > 0 && !watch("hospital_name")) {
      const loc = locations.find(l => String(l.value) === selectedLocId);
      if (loc?.hospitalName) {
        setValue("hospital_name", loc.hospitalName);
      }
    }
  }, [locations, watch("location_ids"), watch("hospital_name"), setValue]);

  // Fetch primary providers based on selected hospital and location
  const watchedHospitalName = watch("hospital_name");
  const watchedLocationId = watch("location_ids");

  useEffect(() => {
    const fetchPrimaryProviders = async () => {
      try {
        setPrimaryProvidersLoading(true);
        let params: any = {};

        if (watchedLocationId) {
          // Find the corresponding branchId from locations context
          const selectedLoc = locations.find(l => String(l.value) === String(watchedLocationId));
          // Pass both location_id (numeric) and branch_id to ensure exact location filtering
          params.location_id = watchedLocationId;
          if (selectedLoc?.branchId) {
            params.branch_id = selectedLoc.branchId;
          }
        } else if (watchedHospitalName) {
          // Find the first location associated with this hospital name to get a hospital ID if needed
          const hospitalLoc = locations.find(l => l.hospitalName === watchedHospitalName);
          if (hospitalLoc?.hospitalId) {
            params.location_id = hospitalLoc.hospitalId;
          }
        } else {
          setPrimaryProviders([]);
          setPrimaryProvidersLoading(false);
          return;
        }

        const data = await AuthService.getProviders(params);
        setPrimaryProviders(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching primary providers:", err);
        setPrimaryProviders([]);
      } finally {
        setPrimaryProvidersLoading(false);
      }
    };

    fetchPrimaryProviders();
  }, [watchedHospitalName, watchedLocationId, locations]);


  // Fetch existing patient and encounter data if returning to this page
  useEffect(() => {
    const fetchExistingData = async () => {
      if (patientId && encounterId && patientId !== "new" && !hasLoadedExistingData) {
        try {
          const patient = await AuthService.getPatient(patientId);
          if (patient) {
            // Skip pre-filling if this is a placeholder record created by NewPatientRedirect
            const isPlaceholder =
              patient.first_name === "Registration" &&
              patient.last_name === "Pending";

            if (!isPlaceholder) {
              setValue("first_name", patient.first_name || "");
              setValue("last_name", patient.last_name || "");
              if (patient.date_of_birth) {
                // Extract just the date part, accounting for timezone issues when converting to Date object
                const dobParts = patient.date_of_birth.split('-');
                if (dobParts.length === 3) {
                  // Creating date using local time to avoid timezone offset issues (e.g. 1990-01-01 becoming 1989-12-31)
                  setValue("date_of_birth", new Date(Number(dobParts[0]), Number(dobParts[1]) - 1, Number(dobParts[2])));
                } else {
                  setValue("date_of_birth", new Date(patient.date_of_birth));
                }
              }
              setValue("gender", patient.gender || "");
              const mobileNumber = patient.mobile_number ? formatPhoneDash(patient.mobile_number) : "";
              setValue("mobile_number", mobileNumber);
              setValue("email", patient.email || "");
              setValue("address_line1", patient.address_line1 || "");
              setValue("address_line2", patient.address_line2 || "");
              setValue("city", patient.city || "");
              setValue("state", patient.state || "");
              setValue("zip", patient.zip || "");

              if (patient.selected_tests && Array.isArray(patient.selected_tests)) {
                setValue("selected_tests", patient.selected_tests);
              }
            }

            // Reverse lookup location -> branch -> hospital
            if (patient.location_ids && patient.location_ids.length > 0) {
              // Simple assignment for location
              setValue("location_ids", String(patient.location_ids[0]));
            }
          }

          const encounters = await AuthService.getPatientEncounters({ patient_id: patientId });
          const currentEncounter = encounters.find((e: any) => e.encounter_id === encounterId);

          if (currentEncounter) {
            const primaryCp = currentEncounter.primary_cp_uuid || currentEncounter.primary_provider_id;
            const secondaryCp = currentEncounter.secondary_cp_uuid || currentEncounter.secondary_provider_id;

            if (primaryCp) {
              setValue("primary_provider_uuid", String(primaryCp));
            }
            if (secondaryCp) {
              setValue("secondary_provider_uuid", String(secondaryCp));
            }
          }
          setHasLoadedExistingData(true);
        } catch (error) {
          console.error("Error fetching existing patient data:", error);
        }
      }
    };

    fetchExistingData();
  }, [patientId, encounterId, setValue, hasLoadedExistingData]);




  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const handleCloseDialog = () => {
    isDialogOpenRef.current = false;
    stopCamera();
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
    setIsRecording(false);
    setRecordingPhase("initial");
    setRecordedVideos([]);
    setStatusMessage("");
    setShowFaceIdModal(false);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      alert("Failed to access camera: " + err);
    }
  };

  const startVideoRecording = async (phase: "initial" | "additional") => {
    if (!cameraStream) return;
    let options: MediaRecorderOptions = {};
    if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
      options = {
        mimeType: "video/webm;codecs=vp8",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
      options = {
        mimeType: "video/webm;codecs=vp9",
        videoBitsPerSecond: 400_000,
      };
    } else if (MediaRecorder.isTypeSupported("video/webm")) {
      options = { mimeType: "video/webm", videoBitsPerSecond: 400_000 };
    } else if (MediaRecorder.isTypeSupported("video/mp4")) {
      options = { mimeType: "video/mp4", videoBitsPerSecond: 400_000 };
    } else {
      options = { videoBitsPerSecond: 400_000 };
    }
    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(cameraStream, options);
    } catch (err) {
      alert("Video recording is not supported in this browser: " + err);
      return;
    }
    let chunks: BlobPart[] = [];
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = async () => {
      if (!isDialogOpenRef.current) return;
      const blob = new Blob(chunks, { type: recorder.mimeType });
      const fileExt = recorder.mimeType.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `recording-${Date.now()}.${fileExt}`, {
        type: recorder.mimeType,
      });
      console.log(
        "Recorded video file size:",
        file.size,
        "bytes (",
        (file.size / 1024 / 1024).toFixed(2),
        "MB )",
      );
      if (phase === "initial") {
        setStatusMessage("Authenticating...");
        const currentUser = await getCurrentUser();
        const username = currentUser?.username;
        if (!username) {
          setStatusMessage("Authentication failed: No active session found");
          alert("Authentication failed: No active session found");
          setRecordingDisabled(false);
          return;
        }
        setRecordingDisabled(true);
        // IMPORTANT: Using user.username as it represents the migrated auth_uuid/identifier
        const loginResult = await biometricAuthService.login(username, true);
        if (!loginResult.success) {
          setStatusMessage(
            "Authentication failed: " +
            (loginResult.error?.message || "Unknown error"),
          );
          alert(loginResult.error?.message || "Authentication failed");
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage("Checking for accessories...");
        const accessoriesCheck =
          await biometricAuthService.detectAccessories(file);
        console.log("Accessory Detection Result:", accessoriesCheck);
        if (accessoriesCheck?.data?.status === "warning") {
          setStatusMessage(
            "Accessories detected! Please remove them and continue.",
          );
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage("Checking if face is detected...");
        const faceSearch = await biometricAuthService.searchFace(file);
        console.log("Face Search Result:", faceSearch);

        if (faceSearch?.data?.matches && faceSearch.data.matches.length > 0) {
          setStatusMessage(
            "A matching face was found. Registration cannot continue.",
          );
          setExistingFaceData(faceSearch.data.matches[0]);
          setShowFaceFoundDialog(true);
          setRecordingDisabled(false);
          return;
        }

        setStatusMessage(
          "Face detected. Proceeding to additional recording...",
        );
        setRecordedVideos([file]);
        setRecordingPhase("additional");
        setTimeout(() => {
          setRecordingDisabled(false);
          startVideoRecording("additional");
        }, 500);
      } else {
        submitVideos([...recordedVideos, file]);
      }
    };
    const durationMs = phase === "initial" ? 3000 : 10000;
    recorder.start();
    setMediaRecorder(recorder);
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingPhase(phase);
    if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
    recordingTimeoutRef.current = setTimeout(() => {
      if (recorder.state === "recording") {
        recorder.stop();
        setIsRecording(false);
        setMediaRecorder(null);
      }
    }, durationMs);
  };

  const stopVideoRecording = () => {
    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const ensurePatientSaved = async (): Promise<string | null> => {
    if (effectivePatientId) return effectivePatientId;

    const params = watch();
    if (!params.first_name?.trim() || !params.last_name?.trim() || !params.date_of_birth || !params.mobile_number?.trim() || !params.gender) {
      toast.error("Please complete the required patient demographics (First Name, Last Name, DOB, Gender, Mobile Number) in Section 1 first.");
      setOpenSection('patient');
      return null;
    }

    setIsCreatingPatient(true);
    try {
      const rawMobile = params.mobile_number.replace(/\D/g, "");
      const cleanMobileNumber = rawMobile.length === 11 && rawMobile.startsWith("1") ? `+${rawMobile}` : `+1${rawMobile}`;
      const cleanEmail = params.email && params.email.trim() !== "" ? params.email.trim() : null;

      const newPatientId = uuidv4();
      const patientData: any = {
        auth_uuid: newPatientId,
        first_name: params.first_name.trim(),
        last_name: params.last_name.trim(),
        middle_name: params.middle_name?.trim() || null,
        social_security_number: params.social_security_number?.trim()?.slice(0, 11) || null,
        mobile_number: cleanMobileNumber,
        email: cleanEmail,
        gender: params.gender,
        date_of_birth: format(params.date_of_birth, "yyyy-MM-dd"),
        enrolled_for_monitorhealth_services: false,
        selected_tests: [],
        status: 'incomplete',
        location_ids: [],
        address_line1: params.address_line1?.trim() || null,
        address_line2: params.address_line2?.trim() || null,
        city: params.city?.trim() || null,
        state: params.state?.trim() || null,
        zip: params.zip?.trim() || null,
        biometric_consent: !!params.biometric_consent,
      };

      const created = await AuthService.createPatient(patientData) as any;
      const finalPatientId = (Array.isArray(created) ? created[0]?.id : created?.id) || newPatientId;
      setCreatedPatientId(finalPatientId);
      markComplete('patient');
      return finalPatientId;
    } catch (err: any) {
      console.error("Failed to ensure patient record:", err);
      toast.error(err.message || "Failed to create patient record. Please verify personal information.");
      return null;
    } finally {
      setIsCreatingPatient(false);
    }
  };

  const submitVideos = async (videos: File[]) => {
    try {
      const video = videos[videos.length - 1];
      console.log(
        "Submitting video file size:",
        video.size,
        "bytes (",
        (video.size / 1024 / 1024).toFixed(2),
        "MB )",
      );
      const formValues = watch();
      const firstName = formValues.first_name || "Unknown";
      const lastName = formValues.last_name || "User";

      let targetPatientId = effectivePatientId;
      if (!targetPatientId) {
        targetPatientId = await ensurePatientSaved();
      }

      if (!targetPatientId) {
        toast.error("Failed to resolve patient ID for Face ID registration.");
        return;
      }

      const response = await biometricAuthService.addFace(
        video,
        firstName,
        targetPatientId,
        lastName,
      );

      if (response.success) {
        setFaceRegistered(true);
        toast.success("Face registered successfully!");
      } else {
        toast.error(response.error?.message || "Face registration failed");
      }
      console.log("Add Face API Result:", response);
    } catch (err) {
      console.error("Error submitting video:", err);
      toast.error("Error submitting video: " + (err as Error).message);
    } finally {
      setShowFaceIdModal(false);
      setRecordingPhase("initial");
      setRecordedVideos([]);
    }
  };

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording]);

  useEffect(() => {
    if (showFaceIdModal) {
      startCamera();
      isDialogOpenRef.current = true;
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [showFaceIdModal]);

  const onSubmit = async (params: RegistrationFormValues) => {
    try {
      clearErrors(["mobile_number", "email"]);
      const queryMobileNumber = `+1${params.mobile_number.replace(/\D/g, "").replace(/^1/, "").slice(-10)}`;
      const saveMobileNumber = queryMobileNumber; // single E.164 format: +1XXXXXXXXXX
      const cleanEmail = params.email?.trim() || null;

      // Check for duplicates (RDS via API)
      const [byEmail, byMobile] = await Promise.all([
        cleanEmail ? AuthService.getPatients(cleanEmail) : [],
        AuthService.getPatients(queryMobileNumber),
      ]);

      const emailMatches = byEmail || [];
      const mobileMatches = byMobile || [];
      const existingPatients = [...emailMatches, ...mobileMatches].filter(
        (p: any) => p.id !== effectivePatientId && (p.email === cleanEmail || p.mobile_number === queryMobileNumber || p.mobile_number === saveMobileNumber)
      );

      if (existingPatients.length > 0) {
        const emailExists = cleanEmail ? existingPatients.some(
          (p) => p.email === cleanEmail,
        ) : false;
        const phoneExists = existingPatients.some(
          (p) => p.mobile_number === queryMobileNumber || p.mobile_number === saveMobileNumber,
        );

        if (emailExists && phoneExists) {
          setError("email", { type: "manual", message: "Email already registered" });
          setError("mobile_number", { type: "manual", message: "Mobile number already registered" });
          throw new Error(
            "A patient with this email and phone number already exists.",
          );
        } else if (emailExists) {
          setError("email", { type: "manual", message: "Email already registered" });
          throw new Error("A patient with this email address already exists.");
        } else if (phoneExists) {
          setError("mobile_number", { type: "manual", message: "Mobile number already registered" });
          throw new Error("A patient with this phone number already exists.");
        }
      }

      console.log("=== FORM DATA ===");
      console.log("first_name:", params.first_name);
      console.log("last_name:", params.last_name);
      console.log(
        "location_ids:",
        params.location_ids,
        "type:",
        typeof params.location_ids,
      );

      console.log("mobile_number:", params.mobile_number);
      console.log("==================");

      // ── Ensure patient record exists ─────────────────────────────────────────
      // When reviewing from the Registration Queue, Section 1 "Save & Continue"
      // was bypassed, so createdPatientId is null. Create the bare patient record
      // here before the rest of the submit flow runs.
      let resolvedPatientId = effectivePatientId;
      if (!resolvedPatientId) {
        console.log("🔄 No patient ID found — creating patient record from queue review...");
        const newPatId = uuidv4();
        const createPayload: any = {
          auth_uuid: newPatId,
          first_name: params.first_name.trim(),
          last_name: params.last_name.trim(),
          middle_name: params.middle_name?.trim() || null,
          social_security_number: params.social_security_number?.trim()?.slice(0, 11) || null,
          mobile_number: saveMobileNumber,
          email: cleanEmail,
          gender: params.gender,
          date_of_birth: format(params.date_of_birth, "yyyy-MM-dd"),
          enrolled_for_monitorhealth_services: false,
          selected_tests: [],
          status: "incomplete",
          location_ids: [],
          address_line1: params.address_line1?.trim() || null,
          address_line2: params.address_line2?.trim() || null,
          city: params.city?.trim() || null,
          state: params.state?.trim() || null,
          zip: params.zip?.trim() || null,
        };
        const newPat = await AuthService.createPatient(createPayload) as any;
        resolvedPatientId = (Array.isArray(newPat) ? newPat[0]?.id : newPat?.id) || newPatId;
        setCreatedPatientId(resolvedPatientId);
        console.log("✅ Patient record created on-the-fly:", resolvedPatientId);
      }
      if (!resolvedPatientId) throw new Error("Failed to create patient record");

      const consentTimestamp = new Date().toISOString();
      const consentLogs = [
        {
          type: "hipaa_consent",
          uuid: resolvedPatientId,
          status: params.hipaa_consent ? "accepted" : "declined",
          details: params.hipaa_consent
            ? "Patient accepted HIPAA compliance consent"
            : "Patient declined HIPAA compliance consent",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
        {
          type: "e_sign_consent",
          uuid: resolvedPatientId,
          status: params.esign_consent ? "accepted" : "declined",
          details: params.esign_consent
            ? "Patient accepted eSign consent"
            : "Patient declined eSign consent",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
        {
          type: "biometric_consent",
          uuid: resolvedPatientId,
          status: params.biometric_consent ? "accepted" : "declined",
          details: params.biometric_consent
            ? "Patient accepted biometric data consent"
            : "Patient declined biometric data consent",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
        {
          type: "sms_consent",
          uuid: resolvedPatientId,
          status: params.sms_consent ? "accepted" : "declined",
          details: "Patient consented to receive SMS notifications",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
        ...(params.email && params.email.trim() !== "" ? [{
          type: "email_consent",
          uuid: resolvedPatientId,
          status: params.email_consent ? "accepted" : "declined",
          details: params.email_consent
            ? "Patient consented to email & SMS communications"
            : "Patient declined email & SMS communications",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        }] : []),
        {
          type: "remote_monitoring_consent",
          uuid: resolvedPatientId,
          status: "accepted", // Default to accepted as per new MHAI consent
          details: "Patient consented to remote health monitoring through MHAI agreement",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
        {
          type: "audio_consent",
          uuid: resolvedPatientId,
          status: "accepted", // Default to accepted
          details: "Patient consented to audio capture for clinical evaluation",
          timestamp: uuidv4(),
          recorded_at: consentTimestamp,
        },
      ];

      const patientUpdateData: any = {
        first_name: params.first_name.trim(),
        last_name: params.last_name.trim(),
        date_of_birth: format(params.date_of_birth, "yyyy-MM-dd"),
        mobile_number: saveMobileNumber,
        gender: params.gender,
        is_face_biometric_registered: faceRegistered,
        enrolled_for_monitorhealth_services: false, // Default to false until tests are assigned
        receive_sms: params.sms_consent,
        email: cleanEmail,
        address_line1: params.address_line1.trim(),
        address_line2: params.address_line2?.trim() || null,
        city: params.city.trim(),
        state: params.state.trim(),
        zip: params.zip.trim(),
        location_ids: params.location_ids ? [Number(params.location_ids)] : [],
        photo_url: params.photo ? "photos" : null,
        selected_tests: [],
        middle_name: params.middle_name?.trim() || null,
        social_security_number: params.social_security_number?.trim()?.slice(0, 11) || null,
        consent_logs: consentLogs,
        primary_cp_uuid: params.primary_provider_uuid,
        secondary_cp_uuid: params.secondary_provider_uuid || null,
        // Individual consent fields for backend consistency
        hipaa_consent: params.hipaa_consent,
        mhai_consent: params.mhai_consent,
        esign_consent: params.esign_consent,
        biometric_consent: params.biometric_consent,
        email_consent: (params.email && params.email.trim() !== "") ? params.email_consent : false,
        sms_consent: params.sms_consent,
        status: 'active',
      };

      // Add auth_uuid to payload if we have an existing one stored in previous patient state or from auth
      // For now, if it's the current user themselves, we might want to link but this is usually staff-facing
      // In the context of the user's request, we want to ensure auth_uuid uses Cognito username.

      console.log("🚀 FINAL UPDATE PAYLOAD:", JSON.stringify(patientUpdateData, null, 2));
      const finalPatientUpdate = await AuthService.updatePatient(resolvedPatientId, patientUpdateData);
      console.log("✅ FINAL UPDATE RESPONSE:", finalPatientUpdate);

      // ── Appointment / Encounter Handling ────────────────────────────────────
      // Per clinical workflow rules, patient registration ONLY registers the patient
      // and assigns their care provider. It does NOT automatically book a scheduled
      // appointment. The telehealth coordinator will schedule appointments separately
      // via the regular Appointments module (/appointments).
      // Only update an existing encounter if we are specifically editing one.
      let finalEncounterId = effectiveEncounterId;
      if (finalEncounterId && finalEncounterId !== "new") {
        const updateData: any = {};
        if (params.location_ids) updateData.location_ids = [Number(params.location_ids)];
        if (params.primary_provider_uuid) updateData.primary_provider_id = params.primary_provider_uuid;
        if (params.secondary_provider_uuid) updateData.secondary_provider_id = params.secondary_provider_uuid;

        if (Object.keys(updateData).length > 0) {
          try {
            console.log("🚀 Updating Encounter:", { id: finalEncounterId, payload: updateData });
            const updateRes = await AuthService.updatePatientEncounter(finalEncounterId as string, updateData);
            console.log("✅ Encounter Updated:", updateRes);
          } catch (encounterError) {
            console.error("Failed to update encounter data:", encounterError);
          }
        }
      }

      // Dispatch registration log report email to Primary and Secondary Providers
      try {
        const primaryProvider = primaryProviders.find(p =>
          String(p.id) === String(params.primary_provider_uuid) ||
          String(p.auth_uuid) === String(params.primary_provider_uuid) ||
          String(p.uuid) === String(params.primary_provider_uuid)
        );
        const secondaryProvider = primaryProviders.find(p =>
          String(p.id) === String(params.secondary_provider_uuid) ||
          String(p.auth_uuid) === String(params.secondary_provider_uuid) ||
          String(p.uuid) === String(params.secondary_provider_uuid)
        );

        const patientFullName = `${params.first_name.trim()} ${params.last_name.trim()}`;
        const dobFormatted = params.date_of_birth ? format(new Date(params.date_of_birth), "MMMM d, yyyy") : "N/A";
        const nowFormatted = format(new Date(), "MMM d, yyyy · h:mm a");

        const selectedLoc = filteredLocations.find(l => String(l.value) === String(params.location_ids));
        const branchLabel = selectedLoc?.label?.split('—')?.[0]?.trim() || selectedLoc?.label || "Pelham Branch";
        const locationDisplay = `In-Person / ${branchLabel}`;

        const formatDrTitle = (p?: any, fallback = "Care Provider") => {
          if (!p) return fallback;
          const raw = p.name || `${p.first_name || ""} ${p.last_name || ""}`.trim();
          if (!raw) return fallback;
          return raw.startsWith("Dr.") || raw.startsWith("Dr ") ? raw : `Dr. ${raw}`;
        };

        const primaryDocName = formatDrTitle(primaryProvider, "Care Provider");
        const secondaryDocName = secondaryProvider ? formatDrTitle(secondaryProvider, "") : undefined;

        // Send to Primary Care Provider
        if (primaryProvider?.email) {
          const primarySubject = `New Patient Registration & Behavioral Health Integration Services Consent for ${primaryDocName} as care provider`;
          const primaryHtml = buildRegistrationEmailHtml({
            providerName: primaryDocName,
            patientId: String(resolvedPatientId),
            patientName: patientFullName,
            dob: dobFormatted,
            gender: params.gender || "Not specified",
            mobile: formatPhoneDisplay(params.mobile_number),
            email: params.email || "",
            registrationDate: nowFormatted,
            acknowledgementDate: nowFormatted,
            location: locationDisplay,
            primaryProviderName: primaryDocName,
            secondaryProviderName: secondaryDocName,
          });

          EmailService.sendEmail({
            to: primaryProvider.email,
            subject: primarySubject,
            body: primaryHtml,
            from: "noreply@monitorhealth.ai",
            from_email: "noreply@monitorhealth.ai",
            sender_name: "MonitorHealth AI",
            from_name: "MonitorHealth AI",
            reply_to: "noreply@monitorhealth.ai",
          }).catch(e => console.error("Failed to send email to primary provider:", e));
        }

        // Send to Secondary Care Provider (if different email)
        if (secondaryProvider?.email && secondaryProvider.email !== primaryProvider?.email) {
          const secDocName = secondaryDocName || "Care Provider";
          const secondarySubject = `New Patient Registration & Behavioral Health Integration Services Consent for ${secDocName} as care provider`;
          const secondaryHtml = buildRegistrationEmailHtml({
            providerName: secDocName,
            patientId: String(resolvedPatientId),
            patientName: patientFullName,
            dob: dobFormatted,
            gender: params.gender || "Not specified",
            mobile: formatPhoneDisplay(params.mobile_number),
            email: params.email || "",
            registrationDate: nowFormatted,
            acknowledgementDate: nowFormatted,
            location: locationDisplay,
            primaryProviderName: primaryDocName,
            secondaryProviderName: secondaryDocName,
          });

          EmailService.sendEmail({
            to: secondaryProvider.email,
            subject: secondarySubject,
            body: secondaryHtml,
            from: "noreply@monitorhealth.ai",
            from_email: "noreply@monitorhealth.ai",
            sender_name: "MonitorHealth AI",
            from_name: "MonitorHealth AI",
            reply_to: "noreply@monitorhealth.ai",
          }).catch(e => console.error("Failed to send email to secondary provider:", e));
        }
      } catch (emailErr) {
        console.error("Error dispatching registration report emails:", emailErr);
      }

      if (reviewingQueueItem?.id && resolvedPatientId) {
        try {
          await AuthService.completeRegistrationQueueItem(reviewingQueueItem.id, String(resolvedPatientId));
          setReviewingQueueItem(null);
          fetchQueueBadge();
        } catch (queueErr) {
          console.error("Failed to mark queue item complete:", queueErr);
        }
      }

      toast.success(
        `Patient ${params.first_name.trim()} ${params.last_name.trim()} registered successfully!`,
        { duration: 4000 },
      );
      // Navigate directly to the Patient Dashboard instead of the Questionnaire screen
      // since new patients do not have any questionnaires assigned yet.
      navigate(`/patients/${resolvedPatientId}`);
    } catch (error: any) {
      console.error("Error saving patient data:", error);
      toast.error(error.message || "Error saving patient data.");
    }
  };

  const onInvalid = (errors: any) => {
    console.error("Form Validation Errors:", errors);
    // Find the first error to display a helpful message
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstError = errors[errorKeys[0]];
      toast.error(`Validation Error: ${firstError.message || "Please check all fields"}`);

      // Auto-open the section with errors if possible
      if (errorKeys.some(k => ["first_name", "last_name", "date_of_birth", "gender", "mobile_number", "email"].includes(k))) {
        setOpenSection('patient');
      } else if (errorKeys.some(k => k.includes("consent"))) {
        setOpenSection('consents');
      } else if (errorKeys.some(k => ["primary_provider_uuid", "location_ids", "hospital_name"].includes(k))) {
        setOpenSection('practitioner');
      }
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue("photo", file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  // ── Accordion helpers ─────────────────────────────────────────────────────
  const toggleSection = (section: 'patient' | 'consents' | 'practitioner') => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const markComplete = (section: string) => {
    setCompletedSections(prev => new Set([...prev, section]));
  };

  // Section header component
  const SectionHeader = ({
    id,
    icon,
    title,
    subtitle,
    badge,
    badgeColor,
  }: {
    id: 'patient' | 'consents' | 'practitioner';
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    badge: string;
    badgeColor: 'green' | 'amber';
  }) => {
    const isOpen = openSection === id;
    const isDone = completedSections.has(id);
    return (
      <button
        type="button"
        onClick={() => toggleSection(id)}
        className="w-full flex items-center gap-4 p-5 text-left transition-colors hover:bg-gray-50/80 rounded-2xl"
      >
        {/* Icon */}
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${id === 'patient' ? 'bg-blue-50' :
          id === 'consents' ? 'bg-emerald-50' :
            id === 'practitioner' ? 'bg-violet-50' : 'bg-amber-50'
          }`}>
          {icon}
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[15px] font-semibold text-gray-900">{title}</span>
            <span className={`text-[11px] font-medium px-2.5 py-0.5 rounded-full border ${badgeColor === 'green'
              ? 'text-gray-600 border-gray-200 bg-gray-50'
              : 'text-amber-700 border-amber-200 bg-amber-50'
              }`}>
              {badge}
            </span>
            {isDone && (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-[13px] text-gray-400 mt-0.5 truncate">{subtitle}</p>
        </div>

        {/* Chevron */}
        <ChevronDown
          className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
        />
      </button>
    );
  };

  // ── Intro video gate ──
  // Rendered inline below (not as an early return) so MainLayout — and the
  // left navigation sidebar — keeps rendering behind it, the same way the
  // old consent carousel dialog worked.

  return (
    <>
      {/* ── Intro video dialog (new patients only) ── */}
      <Dialog open={showVideoIntro} onOpenChange={() => { }}>
        <DialogContent className="sm:max-w-[820px] w-[92vw] p-0 border-0 shadow-2xl bg-black rounded-[2rem] overflow-hidden">
          <DialogTitle className="sr-only">Patient Introduction Video</DialogTitle>
          <DialogDescription className="sr-only">
            Watch the introduction video before starting patient registration.
          </DialogDescription>
          <div className="relative">
            <video
              ref={videoIntroRef}
              className="w-full block max-h-[75vh] object-contain bg-black cursor-pointer"
              src={PATIENT_INTRO_VIDEO_URL}
              playsInline
              onEnded={() => setShowVideoIntro(false)}
              onPlay={() => setVideoIsPlaying(true)}
              onPause={() => setVideoIsPlaying(false)}
              onClick={() => {
                // Clicking the video itself is a direct user gesture, so the
                // browser will always allow play() here — a reliable fallback
                // regardless of whether autoplay succeeded.
                const vid = videoIntroRef.current;
                if (vid) {
                  if (vid.paused) { vid.play().catch(() => { }); }
                  else { vid.pause(); }
                }
              }}
            >
              Your browser does not support embedded video.
            </video>

            {/* Manual play fallback — shown whenever playback isn't actually
              running, regardless of why autoplay didn't kick in. This is the
              reliable path: a direct click always satisfies browser autoplay
              policies, unlike our best-effort automatic attempt below. */}
            {!videoIsPlaying && (
              <button
                type="button"
                onClick={() => {
                  const vid = videoIntroRef.current;
                  if (vid) vid.play().catch(() => { });
                }}
                className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/35 transition-colors"
                aria-label="Play video"
              >
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-black/70 border border-white/40 shadow-lg">
                  <Play className="w-7 h-7 text-white ml-1" fill="currentColor" />
                </span>
              </button>
            )}

            {videoNeedsUnmute && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const vid = videoIntroRef.current;
                  if (vid) vid.muted = false;
                  setVideoNeedsUnmute(false);
                }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-xl bg-black/70 hover:bg-black/85 text-white text-sm font-medium backdrop-blur-sm border border-white/40 shadow-lg transition-colors"
              >
                <VolumeX className="w-4 h-4" />
                Tap to unmute
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowVideoIntro(false);
              }}
              className="absolute top-4 right-4 px-4 py-2 rounded-xl bg-black/70 hover:bg-black/85 text-white text-sm font-medium backdrop-blur-sm border border-white/40 shadow-lg transition-colors"
            >
              Skip
            </button>
          </div>
        </DialogContent>

      </Dialog>
      {/* ── Navigation warning dialog ── */}
      {showLeaveWarning && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 99999,
            background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 16, padding: "32px 28px",
              maxWidth: 420, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 20 }}>⚠️</span>
              </div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>
                Registration not complete
              </h2>
            </div>
            <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
              The patient record has been created but registration is not finished.
              If you leave now, the record will remain <strong>incomplete</strong> and
              will only be visible to Branch Admins until completed.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => { setShowLeaveWarning(false); setPendingNavUrl(null); }}
                style={{
                  padding: "9px 18px", borderRadius: 10, border: "1px solid #e5e7eb",
                  background: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#374151",
                }}
              >
                Stay and finish
              </button>
              <button
                onClick={() => {
                  setShowLeaveWarning(false);
                  if (pendingNavUrl) navigate(pendingNavUrl);
                  else navigate(-1);
                }}
                style={{
                  padding: "9px 18px", borderRadius: 10, border: "none",
                  background: "#dc2626", cursor: "pointer", fontSize: 14, fontWeight: 600, color: "#fff",
                }}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
      <MainLayout showHeaderSelectors={false}>
        <div className="bg-[#f7f8fa] p-6 lg:p-10 font-sans">
          {/* Main Top Navigation Tabs: Register Patient vs Registration Queue */}
          <div className="max-w-4xl mx-auto mb-6">
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-2.5 rounded-2xl border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveMainTab('register')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2",
                    activeMainTab === 'register'
                      ? "bg-[#1a7a4a] text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <User className="w-4 h-4" />
                  Register Patient
                </button>

                <button
                  type="button"
                  onClick={() => setActiveMainTab('queue')}
                  className={cn(
                    "px-5 py-2.5 rounded-xl text-sm font-bold transition-all flex items-center gap-2",
                    activeMainTab === 'queue'
                      ? "bg-[#1a7a4a] text-white shadow-md"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  )}
                >
                  <ListOrdered className="w-4 h-4" />
                  Registration Queue
                  {queueBadgeCount > 0 && (
                    <span className={cn(
                      "text-xs px-2 py-0.5 rounded-full font-bold",
                      activeMainTab === 'queue' ? "bg-white text-[#1a7a4a]" : "bg-green-100 text-green-800"
                    )}>
                      {queueBadgeCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {activeMainTab === 'queue' ? (
            <div className="max-w-4xl mx-auto">
              <RegistrationQueueView
                onReviewAndComplete={handleReviewFromQueue}
                onOpenSendLink={() => setSendLinkModalOpen(true)}
              />
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="max-w-4xl mx-auto">
              <div className="max-w-4xl mx-auto">

                {/* Workflow Mode Toggle */}
                <div className="mb-6 bg-white p-3 rounded-2xl border border-gray-200 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider pl-1">
                      Mode:
                    </span>
                    <div className="flex items-center bg-gray-100 p-1 rounded-xl">
                      <button
                        type="button"
                        onClick={() => {
                          setRegistrationMode('in-clinic');
                          if (isNewRegistration) {
                            setShowVideoIntro(true);
                          }
                        }}
                        className={cn(
                          "px-4 py-2 rounded-lg text-xs font-semibold transition-all",
                          registrationMode === 'in-clinic'
                            ? "bg-white text-gray-900 shadow-sm"
                            : "text-gray-600 hover:text-gray-900"
                        )}
                      >
                        Direct In-Clinic Entry
                      </button>
                      <button
                        type="button"
                        onClick={() => setRegistrationMode('phone-call')}
                        className={cn(
                          "px-4 py-2 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5",
                          registrationMode === 'phone-call'
                            ? "bg-white text-emerald-800 shadow-sm font-bold"
                            : "text-gray-600 hover:text-gray-900"
                        )}
                      >
                        <Phone className="w-3.5 h-3.5 text-emerald-600" />
                        Phone Call / Send Lead Link
                      </button>
                    </div>
                  </div>

                  {registrationMode === 'in-clinic' && isNewRegistration && (
                    <button
                      type="button"
                      onClick={() => setShowVideoIntro(true)}
                      className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-xl border border-gray-200 transition-all font-medium self-end sm:self-auto"
                    >
                      <Play className="w-3.5 h-3.5 text-emerald-600 fill-emerald-600" />
                      <span>Watch Tutorial</span>
                    </button>
                  )}
                </div>

                {/* Phone Call Workflow Hub vs In-Clinic Registration Form */}
                {registrationMode === 'phone-call' && !reviewingQueueItem ? (
                  <div className="space-y-6">
                    {/* Hero Action Card */}
                    <Card className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 shadow-sm">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                        <div className="space-y-2 max-w-xl">
                          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs font-semibold border border-emerald-200">
                            <PhoneCall className="w-3.5 h-3.5 text-emerald-600" />
                            <span>Phone Call Workflow</span>
                          </div>
                          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                            Send Self-Registration Link to Lead
                          </h2>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            While on the phone with the prospective patient, send them a secure 10-minute registration link via SMS or Email. They will watch the introduction video, complete their demographics, and sign consents directly on their own device.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row md:flex-col gap-3 w-full sm:w-auto flex-shrink-0">
                          <Button
                            type="button"
                            onClick={() => setSendLinkModalOpen(true)}
                            className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl h-11 px-6 font-semibold text-sm shadow-md flex items-center justify-center gap-2"
                          >
                            <SendIcon className="w-4 h-4" />
                            Send Link to Lead Now
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setActiveMainTab('queue')}
                            className="rounded-xl border-gray-200 text-gray-700 hover:bg-gray-50 h-11 px-5 text-sm font-medium flex items-center justify-center gap-2"
                          >
                            <ListOrdered className="w-4 h-4 text-[#1a7a4a]" />
                            View Registration Queue {queueBadgeCount > 0 && `(${queueBadgeCount})`}
                          </Button>
                        </div>
                      </div>

                      {/* 3 Step Visual Guide */}
                      <div className="mt-8 pt-8 border-t border-gray-100 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-1.5">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 text-[#1a7a4a] font-bold text-xs flex items-center justify-center">
                            1
                          </div>
                          <h4 className="text-sm font-semibold text-gray-900">Send Link on Call</h4>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            Send via text message or email while speaking with the patient. Link expires in 10 minutes.
                          </p>
                        </div>

                        <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-1.5">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 text-[#1a7a4a] font-bold text-xs flex items-center justify-center">
                            2
                          </div>
                          <h4 className="text-sm font-semibold text-gray-900">Patient Completes Form</h4>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            Patient views the onboarding video, enters their demographics, address, and signs digital consents.
                          </p>
                        </div>

                        <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 space-y-1.5">
                          <div className="w-7 h-7 rounded-lg bg-emerald-100 text-[#1a7a4a] font-bold text-xs flex items-center justify-center">
                            3
                          </div>
                          <h4 className="text-sm font-semibold text-gray-900">Finalize &amp; Enroll</h4>
                          <p className="text-xs text-gray-500 leading-relaxed">
                            Submitted profiles land in your <strong>Registration Queue</strong>. Review and assign Care Practitioner to finish.
                          </p>
                        </div>
                      </div>
                    </Card>

                    {/* Quick Queue Status Banner if items exist */}
                    {queueBadgeCount > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
                            {queueBadgeCount}
                          </div>
                          <div>
                            <h4 className="text-sm font-bold text-emerald-950">
                              {queueBadgeCount} Patient Submission{queueBadgeCount > 1 ? "s" : ""} Ready for Review
                            </h4>
                            <p className="text-xs text-emerald-700">
                              Leads have submitted their forms and are waiting for care practitioner assignment.
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          onClick={() => setActiveMainTab('queue')}
                          className="bg-[#1a7a4a] hover:bg-[#145d39] text-white rounded-xl text-xs font-semibold h-9 px-4 shrink-0 shadow-sm"
                        >
                          Review in Queue &rarr;
                        </Button>
                      </div>
                    )}

                    {/* In-Clinic manual entry switch helper */}
                    <div className="text-center py-2 text-xs text-gray-500">
                      Need to enter patient details manually in-person instead?{" "}
                      <button
                        type="button"
                        onClick={() => {
                          setRegistrationMode('in-clinic');
                          if (isNewRegistration) setShowVideoIntro(true);
                        }}
                        className="text-[#1a7a4a] font-semibold underline hover:text-[#145d39]"
                      >
                        Switch to Direct In-Clinic Entry
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Reviewing Queue Submission Banner */}
                    {reviewingQueueItem && (
                      <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center justify-between gap-3 text-emerald-900 text-xs sm:text-sm">
                        <div className="flex items-center gap-2.5">
                          <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                          <span>
                            <strong>Reviewing Submission:</strong> Finalizing profile for <strong>{reviewingQueueItem.derived_patient_name}</strong>. Demographics &amp; consents pre-filled. Please assign Care Practitioner below to complete enrollment.
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setReviewingQueueItem(null);
                            setCompletedSections(new Set());
                            setOpenSection('patient');
                          }}
                          className="text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 rounded-xl"
                        >
                          Clear Review
                        </Button>
                      </div>
                    )}

                {/* Page header */}
                <div className="mb-8 flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 text-gray-400 hover:text-[#1a7a4a] hover:bg-white rounded-xl transition-all"
                    onClick={() => {
                      if (registrationInProgress) {
                        setPendingNavUrl(null);
                        setShowLeaveWarning(true);
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </Button>
                  <div>
                    <h1 className="text-2xl font-extrabold text-[#1a7a4a] tracking-tight">Patient Registration</h1>
                    <p className="text-sm text-gray-400 mt-0.5">Complete each section and submit when ready</p>
                  </div>
                </div>

                <div className="space-y-3">

                  {/* ── SECTION 1: Patient Information ── */}
                  <Card className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <SectionHeader
                      id="patient"
                      icon={<User className="w-5 h-5 text-blue-400" />}
                      title="Patient information"
                      subtitle="Demographics, contact details, and emergency contact"
                      badge="required"
                      badgeColor="green"
                    />
                    <AnimatePresence initial={false}>
                      {openSection === 'patient' && (
                        <motion.div
                          key="patient-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <CardContent className="px-6 pb-6 pt-0">
                            <div className="h-px bg-gray-100 mb-6" />

                            {/* Personal Information Section */}
                            <div className="mb-10">
                              <div className="flex items-center gap-3 mb-8">
                                <div className="w-12 h-12 bg-[#1a7a4a]/10 rounded-2xl flex items-center justify-center">
                                  <User className="w-6 h-6 text-[#1a7a4a]" />
                                </div>
                                <h2 className="text-2xl font-bold text-gray-900">
                                  Personal Information
                                </h2>
                              </div>

                              <div className="space-y-8">
                                {/* Name Row: First (5), Middle (2), Last (5) */}
                                <div className="grid grid-cols-12 gap-4">
                                  <div className="col-span-12 md:col-span-5 space-y-2">
                                    <Label htmlFor="first_name" className="text-sm font-medium text-gray-700">First Name <span className="text-red-500">*</span></Label>
                                    <div className="relative">
                                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                      <Input id="first_name" placeholder="First Name" className={cn("pl-12 h-14 bg-gray-50 border-gray-100 rounded-2xl", errors.first_name && "border-red-500 bg-red-50")} {...register("first_name")} />
                                    </div>
                                    {errors.first_name && <p className="text-sm text-red-500 mt-1 font-medium">{errors.first_name.message}</p>}
                                  </div>

                                  <div className="col-span-12 md:col-span-2 space-y-2">
                                    <Label htmlFor="middle_name" className="text-sm font-medium text-gray-700">M. Name</Label>
                                    <Input id="middle_name" placeholder="Middle" className={cn("h-14 bg-gray-50 border-gray-100 rounded-2xl", errors.middle_name && "border-red-500 bg-red-50")} {...register("middle_name")} />
                                  </div>

                                  <div className="col-span-12 md:col-span-5 space-y-2">
                                    <Label htmlFor="last_name" className="text-sm font-medium text-gray-700">Last Name <span className="text-red-500">*</span></Label>
                                    <div className="relative">
                                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                      <Input id="last_name" placeholder="Last Name" className={cn("pl-12 h-14 bg-gray-50 border-gray-100 rounded-2xl", errors.last_name && "border-red-500 bg-red-50")} {...register("last_name")} />
                                    </div>
                                    {errors.last_name && <p className="text-sm text-red-500 mt-1 font-medium">{errors.last_name.message}</p>}
                                  </div>
                                </div>

                                {/* SSN Row */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                  <div className="space-y-2">
                                    <Label htmlFor="social_security_number" className="text-sm font-medium text-gray-700">Social Security Number (SSN)</Label>
                                    <Input
                                      id="social_security_number"
                                      placeholder="123-45-6789"
                                      maxLength={11}
                                      className={cn("h-14 bg-gray-50 border-gray-100 rounded-2xl", errors.social_security_number && "border-red-500 bg-red-50")}
                                      {...register("social_security_number")}
                                      onInput={(e) => {
                                        const target = e.target as HTMLInputElement;
                                        let val = target.value.replace(/\D/g, ""); // Only digits
                                        if (val.length > 9) val = val.slice(0, 9);

                                        let formatted = "";
                                        if (val.length > 0) {
                                          formatted = val.slice(0, 3);
                                          if (val.length > 3) {
                                            formatted += "-" + val.slice(3, 5);
                                            if (val.length > 5) {
                                              formatted += "-" + val.slice(5, 9);
                                            }
                                          }
                                        }
                                        target.value = formatted;
                                      }}
                                    />
                                    {errors.social_security_number && <p className="text-sm text-red-500 mt-1 font-medium">{errors.social_security_number.message}</p>}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Contact Information Section */}
                            <div className="mb-8">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <Mail className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">
                                  Contact Information
                                </h2>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Email */}
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="email"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    Email Address
                                  </Label>
                                  <Input
                                    id="email"
                                    type="email"
                                    {...register("email", {
                                      onChange: () => {
                                        if (errors.email) {
                                          clearErrors("email");
                                        }
                                      },
                                    })}
                                    placeholder="Enter your email address"
                                    className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                  />
                                  {errors.email && (
                                    <p className="text-sm text-red-500">
                                      {errors.email.message}
                                    </p>
                                  )}
                                </div>

                                {/* Mobile Number */}
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="mobile_number"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    Mobile Number <span className="text-red-500">*</span>
                                  </Label>
                                  <div className="relative flex items-center">
                                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <span className="absolute left-9 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium text-sm pointer-events-none select-none">+1</span>
                                    <Input
                                      id="mobile_number"
                                      type="text"
                                      placeholder="###-###-####"
                                      maxLength={12}
                                      value={watch("mobile_number") || ""}
                                      onChange={(e) => {
                                        handlePhoneInputChange(e, (formatted) => {
                                          setValue("mobile_number", formatted, { shouldDirty: true, shouldValidate: true });
                                          if (errors.mobile_number) {
                                            clearErrors("mobile_number");
                                          }
                                        }, formatPhoneDash);
                                      }}
                                      onKeyDown={handlePhoneKeyDown}
                                      className="h-12 pl-16 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                      disabled={isSubmitting}
                                    />
                                  </div>
                                  {errors.mobile_number && (
                                    <p className="text-sm text-red-500">
                                      {errors.mobile_number.message}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Address Information Section */}
                            <div className="mb-8">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <MapPin className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">
                                  Address Information
                                </h2>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Address Line 1 */}
                                <div className="space-y-2 md:col-span-2">
                                  <Label
                                    htmlFor="address_line1"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    Address Line 1 <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    id="address_line1"
                                    {...register("address_line1")}
                                    placeholder="Street address, P.O. box, company name, c/o"
                                    className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                  />
                                  {errors.address_line1 && (
                                    <p className="text-sm text-red-500">
                                      {errors.address_line1.message}
                                    </p>
                                  )}
                                </div>

                                {/* Address Line 2 */}
                                <div className="space-y-2 md:col-span-2">
                                  <Label
                                    htmlFor="address_line2"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    Address Line 2
                                  </Label>
                                  <Input
                                    id="address_line2"
                                    {...register("address_line2")}
                                    placeholder="Apartment, suite, unit, building, floor, etc."
                                    className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                  />
                                  {errors.address_line2 && (
                                    <p className="text-sm text-red-500">
                                      {errors.address_line2.message}
                                    </p>
                                  )}
                                </div>

                                {/* City */}
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="city"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    City <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    id="city"
                                    {...register("city")}
                                    placeholder="Enter city"
                                    className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                  />
                                  {errors.city && (
                                    <p className="text-sm text-red-500">
                                      {errors.city.message}
                                    </p>
                                  )}
                                </div>

                                {/* State & Zip Row */}
                                <div className="space-y-2">
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <Label
                                        htmlFor="state"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        State <span className="text-red-500">*</span>
                                      </Label>
                                      <Input
                                        id="state"
                                        {...register("state")}
                                        placeholder="State"
                                        className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                      />
                                      {errors.state && (
                                        <p className="text-sm text-red-500">
                                          {errors.state.message}
                                        </p>
                                      )}
                                    </div>

                                    <div className="space-y-2">
                                      <Label
                                        htmlFor="zip"
                                        className="text-sm font-medium text-gray-700"
                                      >
                                        ZIP Code <span className="text-red-500">*</span>
                                      </Label>
                                      <Input
                                        id="zip"
                                        {...register("zip")}
                                        placeholder="12345 or 12345-6789"
                                        maxLength={10}
                                        onInput={(e) => {
                                          const target = e.target as HTMLInputElement;
                                          // Allow digits and a single hyphen after 5 digits
                                          target.value = target.value
                                            .replace(/[^\d-]/g, "")
                                            .slice(0, 10);
                                        }}
                                        className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl"
                                      />
                                      {errors.zip && (
                                        <p className="text-sm text-red-500">
                                          {errors.zip.message}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Additional Details Section */}
                            <div className="mb-8">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <Calendar className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">
                                  Additional Details
                                </h2>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Date of Birth */}
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-gray-700">
                                    Date of Birth <span className="text-red-500">*</span>
                                  </Label>
                                  <DatePicker
                                    value={watch("date_of_birth")}
                                    onChange={(date) => setValue("date_of_birth", date!)}
                                    placeholder="Select your date of birth"
                                  />
                                  {errors.date_of_birth && (
                                    <p className="text-sm text-red-500">
                                      {errors.date_of_birth.message}
                                    </p>
                                  )}
                                </div>

                                {/* Gender */}
                                <div className="space-y-2">
                                  <Label
                                    htmlFor="gender"
                                    className="text-sm font-medium text-gray-700"
                                  >
                                    Gender <span className="text-red-500">*</span>
                                  </Label>
                                  <Select
                                    onValueChange={(value: "Male" | "Female" | "Other") =>
                                      setValue("gender", value)
                                    }
                                    value={watch("gender")}
                                  >
                                    <SelectTrigger className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl">
                                      <div className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-gray-400" />
                                        <SelectValue placeholder="Select your gender" />
                                      </div>
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="Male">Male</SelectItem>
                                      <SelectItem value="Female">Female</SelectItem>
                                      <SelectItem value="Other">Other</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  {errors.gender && (
                                    <p className="text-sm text-red-500">
                                      {errors.gender.message}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Consents moved to Step 2 */}
                            {/* ── Save & continue ── */}
                            <Button
                              type="button"
                              disabled={isCreatingPatient}
                              onClick={async () => {
                                const step1Fields: (keyof RegistrationFormValues)[] = [
                                  "first_name", "last_name", "middle_name", "social_security_number",
                                  "date_of_birth", "gender", "mobile_number", "email",
                                  "address_line1", "city", "state", "zip",
                                ];
                                clearErrors(["mobile_number", "email"]);
                                const valid = await trigger(step1Fields);
                                if (!valid) return;
                                setIsCreatingPatient(true);
                                try {
                                  const params = watch();
                                  const cleanMobileNumber = `+1${params.mobile_number.replace(/\D/g, "").replace(/^1/, "").slice(-10)}`;
                                  const cleanEmail = params.email?.trim().toLowerCase() || null;
                                  const user = await getCurrentUser();
                                  const profile = await AuthService.getProfile(user.username);
                                  console.log("👤 LOGGED-IN PROFILE:", JSON.stringify(profile, null, 2));

                                  // Duplicate check
                                  const [byEmail, byMobile] = await Promise.all([
                                    cleanEmail ? AuthService.getPatients(cleanEmail) : [],
                                    AuthService.getPatients(cleanMobileNumber),
                                  ]);

                                  console.log("📧 byEmail search result:", JSON.stringify(byEmail, null, 2));
                                  console.log("📱 byMobile search result:", JSON.stringify(byMobile, null, 2));

                                  const existing = [...(byEmail || []), ...(byMobile || [])].filter(
                                    (p: any) => p.id !== effectivePatientId &&
                                      (p.email?.toLowerCase() === cleanEmail || p.mobile_number === cleanMobileNumber)
                                  );

                                  console.log("👥 Existing patients found:", JSON.stringify(existing, null, 2));

                                  if (existing.length > 0) {
                                    const emailExists = cleanEmail ? existing.some(p => p.email?.toLowerCase() === cleanEmail) : false;
                                    const phoneExists = existing.some(p => p.mobile_number === cleanMobileNumber);

                                    if (emailExists && phoneExists) {
                                      setError("email", { type: "manual", message: "Email already registered" });
                                      setError("mobile_number", { type: "manual", message: "Mobile number already registered" });
                                    } else if (emailExists) {
                                      setError("email", { type: "manual", message: "Email already registered" });
                                    } else if (phoneExists) {
                                      setError("mobile_number", { type: "manual", message: "Mobile number already registered" });
                                    }
                                    toast.error("A patient with this email or mobile number already exists");
                                    setIsCreatingPatient(false);
                                    return;
                                  }

                                  const patientData = {
                                    first_name: params.first_name.trim(),
                                    last_name: params.last_name.trim(),
                                    middle_name: params.middle_name?.trim() || null,
                                    social_security_number: params.social_security_number?.trim()?.slice(0, 11) || null,
                                    mobile_number: cleanMobileNumber,
                                    email: cleanEmail,
                                    gender: params.gender,
                                    date_of_birth: format(params.date_of_birth, "yyyy-MM-dd"),
                                    enrolled_for_monitorhealth_services: false,
                                    selected_tests: [],
                                    status: 'incomplete',
                                    location_ids: [], // Deferred to Step 3 to prevent 500 errors during initial creation
                                    address_line1: params.address_line1.trim(),
                                    address_line2: params.address_line2?.trim() || null,
                                    city: params.city.trim(),
                                    state: params.state.trim(),
                                    zip: params.zip.trim(),
                                  } as any;

                                  let finalPatientId = effectivePatientId;
                                  if (finalPatientId) {
                                    await AuthService.updatePatient(finalPatientId, patientData);
                                  } else {
                                    const newPatientId = uuidv4();
                                    patientData.auth_uuid = newPatientId;
                                    console.log("🚀 STEP 1 PAYLOAD:", JSON.stringify(patientData, null, 2));
                                    const created = await AuthService.createPatient(patientData) as any;
                                    console.log("✅ STEP 1 RESPONSE:", created);
                                    finalPatientId = (Array.isArray(created) ? created[0]?.id : created?.id) || newPatientId;
                                    setCreatedPatientId(finalPatientId);
                                  }
                                  markComplete('patient');

                                  setOpenSection('consents');
                                } catch (error: any) {
                                  const errMsg = error.message || "";
                                  if (errMsg.toLowerCase().includes("email")) {
                                    setError("email", { type: "manual", message: "Email already registered" });
                                  }
                                  if (errMsg.toLowerCase().includes("mobile") || errMsg.toLowerCase().includes("number") || errMsg.toLowerCase().includes("phone")) {
                                    setError("mobile_number", { type: "manual", message: "Mobile number already registered" });
                                  }
                                  toast.error(errMsg || "Failed to save personal information. Please try again.");
                                } finally {
                                  setIsCreatingPatient(false);
                                }
                              }}
                              className={cn(
                                "w-full h-12 text-base font-semibold text-white rounded-xl mt-6",
                                "bg-[#1a7a4a] hover:bg-[#145d39] shadow-md flex items-center justify-center gap-2"
                              )}
                            >
                              {isCreatingPatient ? <Loader2 className="animate-spin h-5 w-5" /> : <>Save & Continue <ArrowRight className="w-4 h-4" /></>}
                            </Button>
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>

                  {/* ── SECTION 2: Consents ── */}
                  <Card className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <SectionHeader
                      id="consents"
                      icon={<Star className="w-5 h-5 text-amber-500" />}
                      title="Consents"
                      subtitle="Treatment, communication, and research consents"
                      badge="required"
                      badgeColor="green"
                    />
                    <AnimatePresence initial={false}>
                      {openSection === 'consents' && (
                        <motion.div
                          key="consents-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <CardContent className="px-6 pb-6 pt-0">
                            <div className="h-px bg-gray-100 mb-6" />
                            <div className="flex items-center gap-3 mb-6">
                              <div className="w-10 h-10 bg-amber-50 rounded-2xl flex items-center justify-center">
                                <ShieldCheck className="w-5 h-5 text-amber-500" />
                              </div>
                              <h3 className="text-lg font-bold text-gray-900">Patient Consents</h3>
                            </div>
                            <div className="bg-blue-50/50 rounded-2xl p-8 border border-blue-100 space-y-6">
                              {/* MHAI Consent */}
                              <div className="flex items-start gap-4">
                                <div className="flex items-center h-5 mt-0.5">
                                  <input
                                    id="mhai_consent"
                                    type="checkbox"
                                    {...register("mhai_consent")}
                                    className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label htmlFor="mhai_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                    Monitor Health AI Consent <span className="text-red-500">*</span>
                                  </Label>
                                  <p className="text-sm text-gray-600 mt-2 leading-relaxed italic">
                                    "By selecting the check box, I acknowledge that I have completed the onboarding flow and understand how the services work. I provide my informed consent to participate in these services and agree to the <a href="https://monitorhealth.ai/MonitorHealth_privacy_policy.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Privacy Policy</a> and <a href="#" className="text-blue-600 underline hover:text-blue-800">Terms & Condition</a> of Privacy Practices, which outline how my protected health information is handled. I understand that my participation is voluntary and that I may unenroll from these services at any time through the account settings."
                                  </p>
                                  {errors.mhai_consent && <p className="text-sm text-red-500 mt-2 font-medium">{errors.mhai_consent.message}</p>}
                                </div>
                              </div>

                              <div className="h-px bg-blue-200/50 w-full" />

                              {/* HIPAA Consent */}
                              <div className="flex items-start gap-4">
                                <div className="flex items-center h-5 mt-0.5">
                                  <input
                                    id="hipaa_consent"
                                    type="checkbox"
                                    {...register("hipaa_consent")}
                                    className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <Label htmlFor="hipaa_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                      HIPAA Compliance Consent <span className="text-red-500">*</span>
                                    </Label>
                                    <button
                                      type="button"
                                      onClick={() => setHipaaInfoOpen((v) => !v)}
                                      className="text-blue-400 hover:text-blue-600 transition-colors"
                                    >
                                      {hipaaInfoOpen ? <X className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
                                    </button>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                    I acknowledge that I have read and agree to the HIPAA Notice.
                                  </p>
                                  {hipaaInfoOpen && (
                                    <div className="mt-3 max-h-48 overflow-y-auto rounded-xl border border-blue-200 bg-white p-4 text-xs text-gray-600 space-y-3 shadow-inner">
                                      <p><strong>Notice of Privacy Practices Summary:</strong></p>
                                      <p>We use your PHI for treatment, payment, and healthcare operations. You have the right to inspect your records, request amendments, and receive an accounting of disclosures.</p>
                                    </div>
                                  )}
                                  {errors.hipaa_consent && <p className="text-sm text-red-500 mt-2 font-medium">{errors.hipaa_consent.message}</p>}
                                </div>
                              </div>

                              <div className="h-px bg-blue-200/50 w-full" />

                              {/* eSign Consent */}
                              <div className="flex items-start gap-4">
                                <div className="flex items-center h-5 mt-0.5">
                                  <input
                                    id="esign_consent"
                                    type="checkbox"
                                    {...register("esign_consent")}
                                    className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label htmlFor="esign_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                    eSign Consent <span className="text-red-500">*</span>
                                  </Label>
                                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                    I consent to use electronic signatures and records for my registration and receiving telehealth services. I acknowledge that my electronic signature is legally binding.
                                  </p>
                                  {errors.esign_consent && <p className="text-sm text-red-500 mt-2 font-medium">{errors.esign_consent.message}</p>}
                                </div>
                              </div>

                              <div className="h-px bg-blue-200/50 w-full" />

                              {/* Biometric Consent */}
                              <div className="flex items-start gap-4">
                                <div className="flex items-center h-5 mt-0.5">
                                  <input
                                    id="biometric_consent"
                                    type="checkbox"
                                    {...register("biometric_consent")}
                                    className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label htmlFor="biometric_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                    Biometric Consent <span className="text-gray-400 text-sm font-normal">(optional)</span>
                                  </Label>
                                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                    I consent to the collection and use of my biometric data (facial recognition) for identity verification and access to healthcare services.
                                  </p>
                                </div>
                              </div>

                              <div className="h-px bg-blue-200/50 w-full" />

                              {/* Email Consent */}
                              {watch("email") && watch("email")?.trim() !== "" && (
                                <>
                                  <div className="flex items-start gap-4">
                                    <div className="flex items-center h-5 mt-0.5">
                                      <input
                                        id="email_consent"
                                        type="checkbox"
                                        {...register("email_consent")}
                                        className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <Label htmlFor="email_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                        Email Communication Consent <span className="text-red-500">*</span>
                                      </Label>
                                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                        I consent to receive updates, appointment reminders, and health notifications via email from this healthcare provider.
                                      </p>
                                      {errors.email_consent && <p className="text-sm text-red-500 mt-2 font-medium">{errors.email_consent.message}</p>}
                                    </div>
                                  </div>
                                </>
                              )}

                              <div className="h-px bg-blue-200/50 w-full" />

                              {/* SMS Consent */}
                              <div className="flex items-start gap-4">
                                <div className="flex items-center h-5 mt-0.5">
                                  <input
                                    id="sms_consent"
                                    type="checkbox"
                                    {...register("sms_consent")}
                                    className="w-6 h-6 text-[#1a7a4a] bg-white border-gray-300 rounded-lg focus:ring-[#1a7a4a]"
                                  />
                                </div>
                                <div className="flex-1">
                                  <Label htmlFor="sms_consent" className="text-lg font-bold text-gray-900 cursor-pointer">
                                    SMS Communication Consent <span className="text-red-500">*</span>
                                  </Label>
                                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                                    I consent to receive updates and appointment reminders via SMS text messages. Standard messaging rates may apply.
                                  </p>
                                  {errors.sms_consent && <p className="text-sm text-red-500 mt-2 font-medium">{errors.sms_consent.message}</p>}
                                </div>
                              </div>
                            </div>

                            {/* ── Save & continue ── */}
                            <Button
                              type="button"
                              disabled={isCreatingPatient}
                              onClick={async () => {
                                const consentFields: (keyof RegistrationFormValues)[] = [
                                  "mhai_consent", "hipaa_consent", "esign_consent",
                                  "email_consent", "sms_consent"
                                ];
                                const valid = await trigger(consentFields);
                                if (!valid) return;

                                setIsCreatingPatient(true);
                                try {
                                  const params = watch();
                                  const consentData = {
                                    hipaa_consent: params.hipaa_consent,
                                    email_consent: (params.email && params.email.trim() !== "") ? params.email_consent : false,
                                    sms_consent: params.sms_consent,
                                    e_sign_consent: params.esign_consent,
                                    receive_sms: params.sms_consent,
                                    biometric_consent: params.biometric_consent,
                                  } as any;

                                  let finalPatientId = effectivePatientId;
                                  if (finalPatientId) {
                                    await AuthService.updatePatient(finalPatientId, consentData);
                                  } else {
                                    finalPatientId = await ensurePatientSaved();
                                    if (finalPatientId) {
                                      await AuthService.updatePatient(finalPatientId, consentData);
                                    }
                                  }
                                  markComplete('consents');
                                  setOpenSection('practitioner');
                                } catch (err: any) {
                                  toast.error(err.message || "Failed to save record. Please try again.");
                                } finally {
                                  setIsCreatingPatient(false);
                                }
                              }}
                              className={cn(
                                "w-full h-12 text-base font-semibold text-white rounded-xl mt-6",
                                "bg-[#1a7a4a] hover:bg-[#145d39] shadow-md flex items-center justify-center gap-2"
                              )}
                            >
                              {isCreatingPatient ? <Loader2 className="animate-spin h-5 w-5" /> : <>Save & Continue <ArrowRight className="w-4 h-4" /></>}
                            </Button>
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>

                  {/* ── SECTION 3: Care Practitioner ── */}
                  <Card className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
                    <SectionHeader
                      id="practitioner"
                      icon={<Plus className="w-5 h-5 text-violet-500" />}
                      title="Care practitioner"
                      subtitle="Referring provider and primary care physician"
                      badge="optional"
                      badgeColor="amber"
                    />
                    <AnimatePresence initial={false}>
                      {openSection === 'practitioner' && (
                        <motion.div
                          key="practitioner-body"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          style={{ overflow: 'hidden' }}
                        >
                          <CardContent className="px-6 pb-6 pt-0 space-y-6">
                            <div className="h-px bg-gray-100 mb-2" />

                            {/* Patient Photo & Biometric */}
                            <div>
                              <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <Camera className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">Patient Photo & Biometrics</h2>
                              </div>
                              <div className="bg-gray-50 rounded-2xl p-6">
                                <div className="flex flex-col sm:flex-row items-center gap-4">
                                  {photoPreview && (
                                    <div className="flex-shrink-0">
                                      <img src={photoPreview} alt="Preview" className="w-20 h-20 rounded-full object-cover border-4 border-green-200 shadow-md" />
                                    </div>
                                  )}
                                  <div className="flex-1 space-y-3">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                      <Input id="patientPhotoStep3" type="file" ref={fileInputRef} onChange={handlePhotoChange} accept="image/*" className="hidden" />
                                      <Button type="button" onClick={() => fileInputRef.current?.click()} variant="outline" className="h-12 border-green-200 text-green-700 rounded-xl flex-1">
                                        <Upload className="mr-2 w-5 h-5" /> Upload Photo
                                      </Button>
                                      <div className="flex-1 flex flex-col">
                                        <Button
                                          type="button"
                                          onClick={async () => {
                                            if (faceRegistered) return;
                                            if (!watch("biometric_consent")) {
                                              toast.error("Biometric consent is required to register Face ID. Please enable it in Section 2 (Consents).");
                                              return;
                                            }
                                            let pid = effectivePatientId;
                                            if (!pid) {
                                              pid = await ensurePatientSaved();
                                            }
                                            if (pid) {
                                              setShowFaceIdModal(true);
                                            }
                                          }}
                                          className="h-12 rounded-xl w-full border-green-200 text-green-700"
                                          disabled={faceRegistered || !watch("biometric_consent") || isCreatingPatient}
                                        >
                                          <div className="flex items-center justify-center">
                                            {isCreatingPatient ? (
                                              <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin text-green-600" />
                                                Preparing...
                                              </>
                                            ) : faceRegistered ? (
                                              <>
                                                <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
                                                Face Registered
                                              </>
                                            ) : (
                                              <>
                                                <Camera className="w-5 h-5 mr-2" />
                                                Register Face ID
                                              </>
                                            )}
                                          </div>
                                        </Button>
                                        {!faceRegistered && !watch("biometric_consent") && (
                                          <p className="text-xs text-amber-600 flex items-center gap-1.5 mt-2">
                                            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                            Select &quot;Biometric Consent&quot; in Section 2 to enable Face ID registration
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>


                            {/* Hospital & Location (Added back to Section 3) */}
                            <div>
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <MapPin className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">Assign Location</h2>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                  <Label htmlFor="hospital_name" className="text-sm font-medium text-gray-700">Hospital <span className="text-red-500">*</span></Label>
                                  <Select
                                    onValueChange={(value) => {
                                      setValue("hospital_name", value);
                                      setValue("location_ids", ""); // Reset location when hospital changes
                                      setValue("primary_provider_uuid", ""); // Reset providers
                                      setValue("secondary_provider_uuid", "");
                                    }}
                                    value={watch("hospital_name")}
                                  >
                                    <SelectTrigger className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl">
                                      <SelectValue placeholder="Select Hospital" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {[...new Set(locations.map(l => l.hospitalName))].map(h => (
                                        <SelectItem key={h} value={h}>{h}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {errors.hospital_name && <p className="text-sm text-red-500">{errors.hospital_name.message}</p>}
                                </div>

                                <div className="space-y-2">
                                  <Label htmlFor="location_ids" className="text-sm font-medium text-gray-700">Location <span className="text-red-500">*</span></Label>
                                  <Select
                                    onValueChange={(value) => {
                                      setValue("location_ids", value);
                                      setValue("primary_provider_uuid", ""); // Reset providers when location changes
                                      setValue("secondary_provider_uuid", "");
                                    }}
                                    value={watch("location_ids")}
                                    disabled={!watch("hospital_name")}
                                  >
                                    <SelectTrigger className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl">
                                      <SelectValue placeholder="Select Location" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {filteredLocations.map((loc) => (
                                        <SelectItem key={loc.value} value={String(loc.value)}>
                                          {loc.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {errors.location_ids && <p className="text-sm text-red-500">{errors.location_ids.message}</p>}
                                </div>
                              </div>
                            </div>


                            {/* Primary Provider */}
                            <div>
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <ClipboardPlus className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">Primary Provider</h2>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="primary_provider_uuid" className="text-sm font-medium text-gray-700">
                                  Primary Provider <span className="text-red-500">*</span>
                                </Label>
                                <Select
                                  onValueChange={(value) => setValue("primary_provider_uuid", value)}
                                  value={watch("primary_provider_uuid")}
                                  disabled={isSubmitting || primaryProvidersLoading || !watch("location_ids")}
                                >
                                  <SelectTrigger className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl">
                                    <div className="flex items-center gap-2">
                                      <ClipboardPlus className="w-5 h-5 mr-2" />
                                      <SelectValue placeholder={!watch("location_ids") ? "Please select a location first" : primaryProvidersLoading ? "Loading Primary Provider..." : "Select a Primary Provider"} />
                                    </div>
                                  </SelectTrigger>
                                  <SelectContent>
                                    {primaryProviders.filter(p => p.id && p.id.toString().trim() !== "").map((provider) => {
                                      const roleDisplay = provider.role && provider.role !== "Doctor" ? ` (${provider.role})` : "";
                                      const providerName = provider.name || `${provider.first_name || ""} ${provider.last_name || ""}`.trim() || provider.id;
                                      return (
                                        <SelectItem key={provider.id} value={provider.id.toString()}>
                                          {providerName}{roleDisplay}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                                {errors.primary_provider_uuid && <p className="text-sm text-red-500">{errors.primary_provider_uuid.message}</p>}
                              </div>
                            </div>

                            {/* Secondary Provider */}
                            <div className="mt-6">
                              <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-green-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-800">Secondary Provider (Optional)</h2>
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="secondary_provider_uuid" className="text-sm font-medium text-gray-700">
                                  Secondary Provider
                                </Label>
                                <Select
                                  onValueChange={(value) => setValue("secondary_provider_uuid", value === "none" ? "" : value)}
                                  value={watch("secondary_provider_uuid") || ""}
                                  disabled={isSubmitting || primaryProvidersLoading || !watch("location_ids")}
                                >
                                  <SelectTrigger className="h-12 border-gray-200 focus:border-green-400 focus:ring-green-400 rounded-xl">
                                    <div className="flex items-center gap-2">
                                      <Users className="w-5 h-5 mr-2" />
                                      <SelectValue placeholder={!watch("location_ids") ? "Please select a location first" : primaryProvidersLoading ? "Loading Secondary Provider..." : "Select a Secondary Provider"} />
                                    </div>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">None</SelectItem>
                                    {primaryProviders.filter(p => p.id && p.id.toString().trim() !== "").map((provider) => {
                                      const roleDisplay = provider.role && provider.role !== "Doctor" ? ` (${provider.role})` : "";
                                      const providerName = provider.name || `${provider.first_name || ""} ${provider.last_name || ""}`.trim() || provider.id;
                                      return (
                                        <SelectItem key={provider.id} value={provider.id.toString()}>
                                          {providerName}{roleDisplay}
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectContent>
                                </Select>
                                {errors.secondary_provider_uuid && <p className="text-sm text-red-500">{errors.secondary_provider_uuid.message}</p>}
                              </div>
                            </div>
                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              className={cn(
                                "w-full h-12 text-base font-semibold text-white rounded-xl",
                                "bg-[#1a7a4a] hover:bg-[#145d39] shadow-md flex items-center justify-center gap-2"
                              )}
                            >
                              {isSubmitting ? <Loader2 className="animate-spin h-5 w-5" /> : <>Complete Registration <ArrowRight className="w-4 h-4" /></>}
                            </Button>
                          </CardContent>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>

                </div>
              </>
            )}
          </div>

              {/* Face ID Modal */}
              <Dialog open={showFaceIdModal} onOpenChange={handleCloseDialog}>
                <DialogContent className="max-w-3xl max-h-[95vh] overflow-y-auto rounded-3xl p-6 space-y-6">
                  <DialogHeader>
                    <DialogTitle className="text-2xl font-bold text-green-800">
                      {recordingPhase === "initial"
                        ? "Initial Face Scan"
                        : "Recording Additional Face Details"}
                    </DialogTitle>
                    <DialogDescription className="text-gray-600">
                      {recordingPhase === "initial"
                        ? "Please look directly at the camera for 3 seconds"
                        : "Please move your head slowly in different angles for additional face details"}
                    </DialogDescription>
                  </DialogHeader>

                  {statusMessage && (
                    <div className="mb-4 text-green-700 bg-green-50 px-4 py-3 rounded-xl border border-green-200">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        {statusMessage}
                      </div>
                    </div>
                  )}

                  {/* Camera Container */}
                  <div
                    className="relative bg-gray-900 rounded-2xl overflow-hidden"
                    style={{ minHeight: "400px" }}
                  >
                    <div
                      className="relative w-full"
                      style={{ paddingBottom: "75%" }}
                    >
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="absolute top-0 left-0 w-full h-full object-cover"
                        style={{
                          transform: "scaleX(-1)",
                          minHeight: "400px",
                          maxHeight: "600px",
                        }}
                      />
                    </div>

                    {/* Recording Timer */}
                    <div className="absolute top-4 left-4 bg-black/90 text-white px-4 py-3 rounded-full text-base font-bold flex items-center gap-3 min-w-[80px] shadow-lg border-2 border-white/20">
                      <div
                        className={`w-3 h-3 rounded-full ${isRecording ? "bg-red-500 animate-pulse shadow-lg shadow-red-500/50" : "bg-gray-400"}`}
                      ></div>
                      <span className="text-white font-mono">{recordingTime}s</span>
                    </div>

                    {/* Recording Phase Indicator */}
                    <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-2 rounded-full text-xs font-medium">
                      {recordingPhase === "initial"
                        ? "Face Scan"
                        : "Recording Additional Face Details"}
                    </div>

                    {/* Control Buttons */}
                    <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-4">
                      {isRecording ? (
                        <Button
                          onClick={stopVideoRecording}
                          className="bg-red-500 hover:bg-red-600 text-white h-14 px-8 rounded-xl shadow-lg font-semibold transition-all hover:scale-105"
                        >
                          <div className="w-3 h-3 bg-white rounded-sm mr-3"></div>
                          Stop Recording
                        </Button>
                      ) : (
                        <Button
                          onClick={() => startVideoRecording(recordingPhase)}
                          disabled={recordingDisabled}
                          className="bg-green-500 hover:bg-green-600 text-white h-14 px-8 rounded-xl shadow-lg font-semibold transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
                        >
                          <div className="w-3 h-3 bg-white rounded-full mr-3 animate-pulse"></div>
                          {recordingPhase === "initial"
                            ? "Start Face Scan"
                            : "Start Recording Additional Face Details"}
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                    <p className="font-medium text-gray-800 mb-3">Instructions:</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recordingPhase === "initial" ? (
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Look directly at the camera
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Ensure good lighting
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Remove any accessories
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Keep a neutral expression
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Slowly turn your head left and right
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Tilt your head up and down
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Keep your face in frame
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                            Record for at least 10 seconds
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Face Found Dialog */}
              <Dialog
                open={showFaceFoundDialog}
                onOpenChange={setShowFaceFoundDialog}
              >
                <DialogContent className="max-w-md rounded-3xl">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-red-600" />
                      </div>
                      Face Already Registered
                    </DialogTitle>
                    <DialogDescription className="text-gray-500 text-sm">
                      A patient matching this face profile already exists in the system.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="py-4">
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                      <p className="text-red-800 font-medium mb-3">
                        We found a matching face in our system:
                      </p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">
                            {existingFaceData?.first_name}{" "}
                            {existingFaceData?.last_name}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">ID:</span>
                          <span className="font-mono text-xs">
                            {existingFaceData?.uuid}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Similarity:</span>
                          <span className="font-medium">
                            {existingFaceData?.confidence?.toFixed(2)}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm">
                      Please use a different image or <a
                        href="mailto:admin@monitorhealth.ai?subject=Support%20Request%20-%20Monitor%20Health%20AI&body=Hello%20Support%20Team,%0D%0A%0D%0AI%20need%20assistance%20with%20the%20following%20issue:%0D%0A"
                        className="text-[#1a7a4a] hover:underline"
                      >contact support</a> if this is an error.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => setShowFaceFoundDialog(false)}
                      className="bg-green-500 hover:bg-green-600 text-white px-6 h-10 rounded-xl"
                    >
                      OK
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </form>
          )}

          {/* Send Lead Registration Link Modal (Phone Call Assistance) */}
          <SendLeadRegistrationModal
            open={sendLinkModalOpen}
            onOpenChange={setSendLinkModalOpen}
            onLinkSentSuccess={fetchQueueBadge}
          />
        </div>
      </MainLayout>
    </>
  );
}